import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LoadingButton from "@mui/lab/LoadingButton";

import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import Title from "../../../common/Title";
import {
  __bulkAssignTmobilejFilterGroups,
  __getTmobilejCuratedGroups,
} from "../../../../api/sim";

const lineValidationSchema = yup.object({
  msisdn: yup
    .string("msisdn")
    .min(10, "Should be exactly 10 digits long")
    .max(10, "Should be exactly 10 digits long")
    .required(),
});

const BulkAssignFilterGroups = () => {
  const { setAlertMessage } = useAlert();
  const nav = useNavigate();
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [linesError, setLinesError] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [filterType, setFilterType] = useState("call");
  const [extendCurrentFilters, setExtendCurrentFilters] = useState(false);

  const [groupsResp, getGroups, groupsLoading] = useAxios();
  const [assignResp, assignCall, assignLoading] = useAxios(__bulkAssignTmobilejFilterGroups);

  useEffect(() => {
    getGroups(__getTmobilejCuratedGroups());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (groupsResp?.status && groupsResp?.data) {
      setGroups(groupsResp.data);
    }
  }, [groupsResp]);

  useEffect(() => {
    if (!assignResp?.status) return;
    setAlertMessage({
      type: "success",
      message: `Bulk assign queued. Batch: ${assignResp.data.result}`,
    });
    nav("/tmobilej/filter_group_assign");
  }, [assignResp, nav, setAlertMessage]);

  const validateMsisdnText = (data) => {
    const errors = {};
    const formatted = [];
    if (!data) return { errors, formatted };

    const seen = [];
    const lines = data.split("\n");
    lines.some((line, idx) => {
      const value = String(line || "").trim();
      if (!value) return false;
      try {
        const obj = { msisdn: value };
        lineValidationSchema.validateSync(obj);
        if (isNaN(obj.msisdn)) throw new Error("Invalid Number");
        formatted.push(obj);
        seen.push(obj.msisdn);
        if (seen.indexOf(obj.msisdn) !== seen.length - 1) {
          errors.lines = `Duplicate msisdn value in line ${idx + 1}`;
          return true;
        }
      } catch (e) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }
      return false;
    });
    return { errors, formatted };
  };

  const handleTextChange = (value) => {
    setRawData(value);
    const { errors, formatted } = validateMsisdnText(value);
    setLinesError(errors.lines || "");
    setLinesData(errors.lines ? [] : formatted);
  };

  const handleSubmit = async () => {
    if (!selectedGroupIds.length) {
      setAlertMessage({ type: "error", message: "Select at least one filter group" });
      return;
    }
    const { errors, formatted } = validateMsisdnText(rawData);
    if (errors.lines || !formatted.length) {
      setLinesError(errors.lines || "Add at least one valid msisdn");
      return;
    }
    await assignCall({
      ...__bulkAssignTmobilejFilterGroups,
      data: {
        lines: formatted,
        selected_group_ids: selectedGroupIds,
        filter_type: filterType,
        extend_current_filters: extendCurrentFilters,
      },
    });
  };

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      const text = target?.result || "";
      handleTextChange(text);
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    if (!file.name.endsWith(".csv")) {
      setAlertMessage({ type: "error", message: "Please input a csv file" });
      return;
    }
    handleFile(file);
  };

  return (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      alignItems="center"
      flexDirection="column"
    >
      <Title
        variant="h5"
        content="Bulk Assign Filter Groups"
        sx={{ mb: 3 }}
        gutterBottom
      />

      <Grid
        container
        spacing={2}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item xs={12} minWidth={550}>
          <FormControl fullWidth>
            <InputLabel id="bulk-filter-type-label">Filter Type</InputLabel>
            <Select
              labelId="bulk-filter-type-label"
              value={filterType}
              label="Filter Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="call">Call Filter</MenuItem>
              <MenuItem value="message">Message Filter</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} minWidth={550}>
          <FormControl fullWidth>
            <InputLabel id="bulk-groups-label">Curated Groups</InputLabel>
            <Select
              labelId="bulk-groups-label"
              multiple
              value={selectedGroupIds}
              onChange={(e) => setSelectedGroupIds(e.target.value)}
              input={<OutlinedInput label="Curated Groups" />}
              renderValue={(selected) =>
                groups
                  .filter((g) => selected.includes(g.id))
                  .map((g) => g.name)
                  .join(", ")
              }
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  <Checkbox checked={selectedGroupIds.indexOf(group.id) > -1} />
                  <ListItemText primary={group.name} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select one or more curated groups.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} minWidth={550}>
          <FormControl fullWidth>
            <FormControlLabel
              control={
                <Checkbox
                  checked={extendCurrentFilters}
                  onChange={(e) => setExtendCurrentFilters(e.target.checked)}
                />
              }
              label="Extend current filters"
            />
            <FormHelperText>
              {extendCurrentFilters
                ? "Selected groups will be added to each line's existing filter groups and blocked numbers."
                : "Selected groups will replace each line's existing filter groups (their numbers are removed). Personal blocked numbers are kept."}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item>
          <FormControl sx={{ width: "100%" }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ mr: 1 }}
            >
              Upload CSV
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </FormControl>
        </Grid>

        <Grid item>
          <FormControl error={Boolean(linesError)} sx={{ width: "100%" }}>
            <FormHelperText id="lines-helper-text">
              Add one MSISDN per line (10 digits)
            </FormHelperText>
            <TextField
              multiline
              minRows={8}
              placeholder="1234567890"
              value={rawData}
              onChange={(e) => handleTextChange(e.target.value)}
              error={Boolean(linesError)}
              helperText={linesError}
              sx={{ minWidth: 550 }}
            />
          </FormControl>
        </Grid>

        <Grid
          container
          sx={{ mt: 3 }}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item>
            <LoadingButton
              loading={assignLoading || groupsLoading}
              variant="contained"
              disabled={
                Boolean(linesError) ||
                !linesData.length ||
                !selectedGroupIds.length ||
                groupsLoading
              }
              onClick={handleSubmit}
            >
              Assign Filter Groups
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BulkAssignFilterGroups;
